'use client'

import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { createClient } from '@/utils/supabase/client'
import { apiFetch } from '@/lib/api'
import { ConnectivityService } from '@/lib/connectivity'
import { useAnalytics } from '@/hooks/useAnalytics'
import { clearCachedInternalUserId } from '@/hooks/useCurrentInternalUserId'
import { clearCachedInternalUserIdInCreateAnalysis } from '@/lib/create-analysis'
import { cacheUserLocally, getCachedUser, clearCachedUser } from '@/lib/auth-cache'
import { hardNavigate } from '@/lib/hard-nav'
import type { User } from '@supabase/supabase-js'

interface AuthContextType {
  user: User | null
  userRole: string | null
  loading: boolean
  isOffline: boolean
  // False until ConnectivityService has settled its first probe. Consumers that
  // shouldn't render an "offline" state on the pessimistic native seed (before
  // we've actually confirmed connectivity) gate on this — e.g. the offline
  // banner, so it doesn't flash on every native cold start.
  connectivityVerified: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isOffline, setIsOffline] = useState(false)
  const [connectivityVerified, setConnectivityVerified] = useState(false)
  const supabase = createClient()
  const { identifyUser, trackAuth, resetAnalytics } = useAnalytics()

  // Fast path — runs synchronously before first paint, so reloads with a
  // cached user skip the splash entirely. The full auth init below still
  // runs in the background to refresh session data.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return
    const cached = getCachedUser()
    if (cached) {
      setUser(cached.user)
      setUserRole(cached.role)
      setLoading(false)
    }
  }, [])

  // Initialize auth — everything races against a hard 4s deadline.
  // Supabase getSession() can hang offline (token refresh), so we never trust it to complete.
  useEffect(() => {
    let resolved = false
    const finish = (authUser: User | null, role: string | null) => {
      if (resolved) return
      resolved = true
      setUser(authUser)
      setUserRole(role)
      setLoading(false)
    }

    // Hard deadline — no matter what, stop loading after 4s
    const deadline = setTimeout(() => {
      if (!resolved) {
        console.warn('[AuthProvider] Deadline hit — using cached user or null')
        const cached = getCachedUser()
        finish(cached?.user ?? null, cached?.role ?? null)
      }
    }, 4000)

    ;(async () => {
      try {
        // Offline fast path — navigator.onLine is synchronous.
        // Skip all network calls; use cached user immediately.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          const cached = getCachedUser()
          finish(cached?.user ?? null, cached?.role ?? null)
          return
        }

        // getSession reads from local storage but may try to refresh the token (network call).
        // Race it against 2s. If it hangs, fall back to cached user.
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<Awaited<ReturnType<typeof supabase.auth.getSession>> | null>(r => setTimeout(() => r(null), 2000)),
        ])
        const session = sessionResult?.data?.session ?? null

        if (session?.user) {
          finish(session.user, session.user.user_metadata?.role || null)
          // Validate in background (fire-and-forget, non-blocking)
          supabase.auth.getUser().then(({ data: { user: v } }) => {
            if (v) {
              setUser(v)
              getUserRoleFromJWT(v)
            } else if (navigator.onLine) {
              // Server said "no user" for a token we thought was valid —
              // session was revoked. Drop the stale cache or /login's cached
              // user triggers router.replace('/detect'), middleware bounces
              // back, and we white-screen in a loop.
              setUser(null)
              setUserRole(null)
              clearCachedUser()
            }
          }).catch(() => {})
          return
        }

        // No session — try cached user
        const cached = getCachedUser()
        if (cached) {
          finish(cached.user, cached.role)
          return
        }

        // No cache — nothing we can do offline. Online: try getUser with timeout.
        if (navigator.onLine) {
          const userResult = await Promise.race([
            supabase.auth.getUser(),
            new Promise<Awaited<ReturnType<typeof supabase.auth.getUser>> | null>(r => setTimeout(() => r(null), 2000)),
          ])
          const networkUser = userResult?.data?.user ?? null
          if (networkUser) {
            getUserRoleFromJWT(networkUser)
            finish(networkUser, networkUser.user_metadata?.role || null)
            return
          }
        }

        // Truly no user
        finish(null, null)
      } catch {
        const cached = getCachedUser()
        finish(cached?.user ?? null, cached?.role ?? null)
      }
    })()

    return () => clearTimeout(deadline)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const svc = ConnectivityService.getInstance()
    return svc.subscribe((online: boolean) => {
      setIsOffline(!online)
      setConnectivityVerified(svc.verifiedOnce)
    })
  }, [])

  // Get user role from JWT metadata (NO HTTP CALL!)
  const getUserRoleFromJWT = (currentUser: User) => {
    try {
      const role = currentUser.user_metadata?.role || null
      setUserRole(role)

      // Cache user locally for offline access
      cacheUserLocally(currentUser, role)

      // Identify user in PostHog
      identifyUser(currentUser.id, {
        role: role,
        company: currentUser.user_metadata?.company,
      })
    } catch (error) {
      console.error('Error reading user role from JWT:', error)
      setUserRole(null)
    }
  }

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthProvider] Auth event:', event)

      if (event === 'SIGNED_IN') {
        const newUser = session?.user ?? null
        // Drop any cached internal_user_id from a prior session before the
        // first read on this login — without this, a stale value from a
        // previous user (closed-tab session, expired refresh, etc.) leaks
        // into createAnalysis/useProjects and the new user writes rows
        // attributed to the old one.
        clearCachedInternalUserId()
        clearCachedInternalUserIdInCreateAnalysis()
        setUser(newUser)
        trackAuth('login', { userId: newUser?.id })

        if (newUser) {
          getUserRoleFromJWT(newUser)
        }

        // Auto-request push notification permission on login
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
          try {
            const { requestNotificationPermission, saveFCMToken } = await import('@/lib/firebase')
            const token = await requestNotificationPermission()
            if (token && newUser?.id) {
              await saveFCMToken(token, newUser.id)
              console.log('[AuthProvider] Push notifications auto-enabled')

              if (ConnectivityService.getInstance().isOnline) {
                try {
                  await apiFetch('/api/notifications/welcome', {
                    method: 'POST',
                    credentials: 'include'
                  })
                } catch {
                  // welcome is non-critical; retried on next login
                }
              }
            }
          } catch (error) {
            console.error('[AuthProvider] Failed to auto-enable push notifications:', error)
          }
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setUserRole(null)
        clearCachedUser()
        clearCachedInternalUserId()
        clearCachedInternalUserIdInCreateAnalysis()
        trackAuth('logout')
        resetAnalytics()
      }
    })

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, trackAuth, resetAnalytics, identifyUser])

  const signOut = async () => {
    clearCachedUser()
    clearCachedInternalUserId()
    // scope: 'local' tears down this browser/device only. Default 'global'
    // revokes the user's refresh token across every signed-in session, which
    // is rarely what mobile/desktop users want and breaks parallel E2E tests
    // that share a permanent fixture user (one worker logging out kills the
    // others' sessions mid-flight).
    await supabase.auth.signOut({ scope: 'local' })
    // hardNavigate closes PowerSync's native connection before the full load.
    // Without it, sign-out abandons the open imki.sqlite connection and the
    // collision lands on the NEXT login's fresh instance → code 5 on native.
    await hardNavigate('/')
  }

  return (
    <AuthContext.Provider value={{ user, userRole, loading, isOffline, connectivityVerified, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
